import React, { useState, useEffect, useRef } from "react"
import { useRouter, withRouter } from "next/router"
import _ from "lodash"
import ReactMapboxGl from "react-mapbox-gl"
import { getLayerImages } from "../../utilities/map"
import { mapStyle, mapAccessToken } from "../../config/map"
import MapPopup from "./MapPopup"
import usePrevious from "../../common/hooks/usePrevious"
import MoveableMarkers from "./MoveableMarkers"
import HoverMarker from "./HoverMarker"
import { getZoomFromType } from "../../utilities/map"
import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  IconButton,
  useTheme,
} from "@chakra-ui/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faList,
  faLocation,
  faMap,
  faSpinnerThird,
} from "@fortawesome/pro-light-svg-icons"
import NearbyList from "./NearbyList"
import NextLink from "next/link"
import Tooltip from "../../common/components/Tooltip"
import useColors from "../../common/hooks/useColors"

const layerImages = getLayerImages()

let Map = ReactMapboxGl({
  accessToken: mapAccessToken,
})

const GuidesMap = ({
  loggedInUser,
  moveableFeatures = [],
  params = {},
  userLocation,
  selectedFeature,
  changePosition,
  getNearbyFeatures,
  featureSlugType,
  onSelect = () => {},
  onDeselect = () => {},
  onClick = () => {},
  initGuides = [],
}) => {
  const { blueColor, whiteColor } = useColors()
  const router = useRouter()
  const [displayView, setDisplayView] = useState(router.query.view || "map")
  useEffect(() => {
    setDisplayView(router.query.view || "map")
  }, [router.query.view])
  const mapRef = useRef()
  const [isSelectingFromMap, setIsSelectingFromMap] = useState(false)
  const [mapPosition, setMapPosition] = useState({
    zoom: params.zoom ? [params.zoom] : [2],
    coordinates:
      params.latitude && params.longitude
        ? [params.longitude, params.latitude]
        : [userLocation.longitude, userLocation.latitude],
  })
  const [curCenter, setCurCenter] = useState(mapPosition.coordinates)
  const [curZoom, setCurZoom] = useState(mapPosition.zoom)
  // const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [hoverFeature, setHoverFeature] = useState()
  const [popupMarker, setPopupMarker] = useState(selectedFeature)
  const [isMapMoved, setIsMapMoved] = useState(false)
  const [isMarkersDraggable, setIsMarkersDraggable] = useState(false)
  const [layerIds, setLayerIds] = useState([])
  const prevFeature = usePrevious(selectedFeature)
  useEffect(() => {
    if (!_.isEqual(prevFeature, selectedFeature) && selectedFeature) {
      if (isSelectingFromMap) {
        setIsSelectingFromMap(false)
        return
      }
      setPopupMarker(selectedFeature)
      setMapPosition({
        zoom: getZoomFromType(selectedFeature.type),
        coordinates: selectedFeature.location.coordinates,
      })
    }
  }, [isSelectingFromMap, prevFeature, selectedFeature])

  const prevLocation = usePrevious(userLocation)
  useEffect(() => {
    if (
      !params.latitude &&
      !_.isEqual(prevLocation, userLocation) &&
      !selectedFeature &&
      !moveableFeatures?.length
    ) {
      setMapPosition({
        zoom: userLocation.longitude ? [9] : [2],
        coordinates: [userLocation.longitude, userLocation.latitude],
      })
    }
  }, [
    isMapMoved,
    moveableFeatures?.length,
    params.latitude,
    prevLocation,
    selectedFeature,
    userLocation,
  ])
  const moveableFeatureSlugs = moveableFeatures.map((f) => f?.slug)
  const firstMoveableLayerId = layerIds.find((id) => id.includes("moveable"))
  const firstSelectedLayerId = layerIds.find((id) => id.includes("selected"))
  const flatFeatures = getNearbyFeatures
    ? Object.values(_.omit(getNearbyFeatures, "__typename"))?.flat()
    : []
  const nearbyFeatures =
    flatFeatures?.length !== 0
      ? flatFeatures.filter(
          (feat) =>
            selectedFeature?.slug !== feat?.slug &&
            !moveableFeatureSlugs.includes(feat?.slug),
        )
      : initGuides
  // find the feature if have a feature slug type and select it
  useEffect(() => {
    if (featureSlugType) {
      const feature = [
        ...(nearbyFeatures || []),
        ...(moveableFeatures || []),
      ].find((f) => f.slug + "-" + f.type === featureSlugType)
      if (feature && feature?.slug !== selectedFeature?.slug) {
        onSelect(feature)
      }
    }
  }, [
    featureSlugType,
    moveableFeatures,
    nearbyFeatures,
    onSelect,
    selectedFeature?.slug,
  ])
  const hasLocation =
    !userLocation.error &&
    userLocation.latitude &&
    !featureSlugType &&
    !selectedFeature &&
    !params.latitude
  return (
    <Box flex={1} alignSelf="stretch" minHeight={600}>
      <ButtonGroup zIndex={10} position="absolute" top={"15px"} left={5}>
        {hasLocation ? (
          <IconButton
            variant="secondary"
            raised="true"
            onClick={() => {
              if (userLocation.error) {
                if (!navigator.geolocation) {
                  alert("Geolocation is not supported by this browser.")
                  return
                }
                // just check location again
                userLocation.setNumTimesChecked((ind) => ind + 1)
              } else {
                setMapPosition({
                  zoom: [15],
                  coordinates: [userLocation.longitude, userLocation.latitude],
                })
              }
            }}
          >
            <FontAwesomeIcon size="lg" color={blueColor} icon={faLocation} />
          </IconButton>
        ) : (
          <Button
            variant="secondary"
            raised="true"
            rightIcon={
              <FontAwesomeIcon
                size="lg"
                icon={faSpinnerThird}
                spin
                color={blueColor}
              />
            }
          >
            Finding Location...
          </Button>
        )}
        <NextLink shallow href="/map?view=map" passHref>
          <IconButton
            data-tip
            data-tooltip-id="map-view-tt"
            variant={displayView === "map" ? "primary" : "secondary"}
            raised="true"
            as="a"
            href={"/map?view=map"}
          >
            <FontAwesomeIcon
              size="lg"
              color={displayView === "map" ? whiteColor : blueColor}
              icon={faMap}
            />
          </IconButton>
        </NextLink>
        <NextLink shallow href="/map?view=list" passHref>
          <IconButton
            data-tip
            data-tooltip-id="list-view-tt"
            variant={displayView === "list" ? "primary" : "secondary"}
            raised="true"
            as="a"
          >
            <FontAwesomeIcon
              size="lg"
              color={displayView === "list" ? whiteColor : blueColor}
              icon={faList}
            />
          </IconButton>
        </NextLink>
      </ButtonGroup>
      <Tooltip place="bottom" class="card-tooltip" id={"list-view-tt"}>
        <Heading as="h6">
          {displayView === "map"
            ? "Show the current features as a list"
            : "Viewing as a list"}
        </Heading>
      </Tooltip>
      <Tooltip place="bottom" class="card-tooltip" id={"map-view-tt"}>
        <Heading as="h6">
          {displayView === "map" ? "Viewing as a map" : "Back to map"}
        </Heading>
      </Tooltip>
      {/* {loggedInUser?.isAdmin && (
        <Button
          position="absolute"
          className={
            styles["moveable-btn"]
          }
          raised="true"
          onClick={() => {
            setIsMarkersDraggable((isD) => !isD)
          }}
        >
          {" "}
          <FontAwesomeIcon
            size="lg"
            color={isMarkersDraggable ? whiteColor : blueColor}
            icon={faHandRock}
          />
        </Button>
      )} */}
      <Box display={displayView === "map" ? "block" : "none"} height="100%">
        <Map
          style={mapStyle}
          accessToken={mapAccessToken}
          containerStyle={{
            width: "100%",
            height: "100%",
          }}
          center={mapPosition.coordinates}
          zoom={mapPosition.zoom}
          onClick={() => {
            if (window.innerWidth < 480 && onClick) {
              onClick()
            }
          }}
          onMoveEnd={(map) => {
            mapRef.current = map
            const center = map.getCenter()
            const zm = map.getZoom()
            const bounds = map.getBounds()
            setCurCenter(center)
            setCurZoom([zm])
            setIsMapMoved(true)
            changePosition(
              bounds,
              {
                type: "Point",
                coordinates: [center.lng, center.lat],
              },
              zm,
            )
          }}
          onStyleData={(map) => {
            const center = map.getCenter()
            const zm = map.getZoom()
            const bounds = map.getBounds()
            setCurCenter(center)
            setCurZoom([zm])
            changePosition(
              bounds,
              {
                type: "Point",
                coordinates: [center.lng, center.lat],
              },
              zm,
            )
          }}
        >
          {
            /* load in any areas and guides based on current map position */
            nearbyFeatures ? (
              <MoveableMarkers
                mapRef={mapRef}
                layerIds={layerIds}
                setLayerIds={setLayerIds}
                before={
                  moveableFeatures?.length
                    ? firstMoveableLayerId
                    : selectedFeature
                    ? firstSelectedLayerId
                    : null
                }
                layerId="nearby"
                isDraggable={loggedInUser?.isAdmin && isMarkersDraggable}
                feats={nearbyFeatures}
                layerImages={layerImages}
                setTooltip={(feat) => {
                  setHoverFeature(feat)
                }}
                clearTooltip={() => {
                  setHoverFeature(null)
                }}
                onClick={(feat) => {
                  setIsSelectingFromMap(true)
                  onSelect(feat)
                  setPopupMarker(feat)
                  setMapPosition({
                    zoom: curZoom,
                    coordinates: [curCenter.lng, curCenter.lat],
                  })
                }}
              />
            ) : null
          }
          {selectedFeature ? (
            <MoveableMarkers
              layerIds={layerIds}
              setLayerIds={setLayerIds}
              layerId="selected"
              isDraggable={loggedInUser?.isAdmin && isMarkersDraggable}
              feats={[selectedFeature]}
              isPrimary
              layerImages={layerImages}
              setTooltip={(feat) => {
                setHoverFeature(feat)
              }}
              clearTooltip={() => {
                setHoverFeature(null)
              }}
              onClick={(feat) => {
                setIsSelectingFromMap(true)
                setPopupMarker(feat)
              }}
            />
          ) : null}
          {!!moveableFeatures?.length && (
            <MoveableMarkers
              layerIds={layerIds}
              setLayerIds={setLayerIds}
              layerId="moveable"
              isPrimary
              isDraggable
              feats={moveableFeatures}
              layerImages={layerImages}
              setTooltip={(feat) => {
                setHoverFeature(feat)
              }}
              clearTooltip={() => {
                setHoverFeature(null)
              }}
              onClick={(feat) => {
                setIsSelectingFromMap(true)
                onSelect(feat)
                setPopupMarker(feat)
              }}
            />
          )}
          {!!popupMarker ? (
            <MapPopup
              popupCoords={popupMarker?.location?.coordinates}
              popupMarker={popupMarker}
              closePopup={() => {
                setPopupMarker(null)
                if (onDeselect) {
                  onDeselect()
                }
              }}
            />
          ) : null}
          {!!hoverFeature && <HoverMarker feature={hoverFeature} />}
        </Map>
      </Box>
      {displayView === "list" && (
        <NearbyList
          features={[
            ...(nearbyFeatures ?? []),
            ...(moveableFeatures ?? []),
            selectedFeature,
          ]}
        />
      )}
    </Box>
  )
}

export default withRouter(GuidesMap)
